import {
  createSlice
} from '@reduxjs/toolkit'
import {
  fetchDashboardData,
  fetchBrowserViews,
  fetchDesktopViews,
  fetchMobileViews,
  fetchOtherViews,
  fetchMonthlyPlatformBreakdown,
  fetchHourlyPropertyViews,
  fetchHourlySessions,
  fetchAllAnalytics,
  fetchWorldMap,
  fetchKPIData,
  fetchConversionByDevice,
  fetchAppDownloads,
  fetchSalesConversionPath,
  fetchWebsiteAnalytics,
  fetchWebsiteMetrics,
  fetchAppMetrics,
  fetchUserTypeBreakdown
} from '../actions/dashboardActions.js'

const initialState = {
  metrics: {
    totalProperties: 0,
    totalUsers: 0,
    totalAgents: 0,
    totalTransactions: 0,
    totalLeads: 0,
  },
  properties: [],
  activities: [],
  browserViews: [],
  desktopViews: [],
  mobileViews: [],
  otherViews: [],
  monthlyPlatformBreakdown: [],
  hourlyPropertyViews: [],
  hourlySessions: [],
  allAnalytics: {},
  worldMap: [],
  // KPI Data
  kpiData: {
    revenue: {
      total: 0,
      growth: 0
    },
    users: {
      total: 0,
      active: 0,
      new: 0
    },
    properties: {
      totalListings: 0,
      activeListing: 0,
      sold: 0
    },
    agents: {
      total: 0,
      active: 0,
      topPerformer: ''
    }
  },
  conversionByDevice: [],
  appDownloads: {
    total: 0,
    byUserType: {
      premium: 0,
      free: 0,
      trial: 0
    },
    byPlatform: {
      iOS: 0,
      Android: 0,
      Web: 0
    }
  },
  salesConversionPath: {},
  websiteAnalytics: {
    conversionRate: 0,
    visitors: 0,
    pageViews: 0,
    downloads: 0,
    clicks: 0
  },
  websiteMetrics: {
    visits: 0,
    clicks: 0,
    downloads: 0,
    visitsGrowth: 0,
    clicksGrowth: 0,
    downloadsGrowth: 0
  },
  appMetrics: {
    downloads: 0,
    registered: 0,
    clicks: 0,
    downloadsGrowth: 0,
    registeredGrowth: 0,
    clicksGrowth: 0
  },
  userTypeBreakdown: {
    mavenBuyer: 0,
    mavenBuyerGrowth: 0
  },
  loading: false,
  error: null,
}

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchDashboardData.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchDashboardData.fulfilled, (state, action) => {
        state.metrics = action.payload.metrics || state.metrics
        state.properties = action.payload.properties || state.properties
        state.activities = action.payload.activities || state.activities
        state.loading = false
      })
      .addCase(fetchDashboardData.rejected, (state, action) => {
        state.error = action.error.message
        state.loading = false
      })

      .addCase(fetchBrowserViews.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchBrowserViews.fulfilled, (state, action) => {
        state.browserViews = action.payload
        state.loading = false
      })
      .addCase(fetchBrowserViews.rejected, (state, action) => {
        state.error = action.error.message
        state.loading = false
      })

      .addCase(fetchDesktopViews.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchDesktopViews.fulfilled, (state, action) => {
        state.desktopViews = action.payload
        state.loading = false
      })
      .addCase(fetchDesktopViews.rejected, (state, action) => {
        state.error = action.error.message
        state.loading = false
      })

      .addCase(fetchMobileViews.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchMobileViews.fulfilled, (state, action) => {
        state.mobileViews = action.payload
        state.loading = false
      })
      .addCase(fetchMobileViews.rejected, (state, action) => {
        state.error = action.error.message
        state.loading = false
      })

      .addCase(fetchOtherViews.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchOtherViews.fulfilled, (state, action) => {
        state.otherViews = action.payload
        state.loading = false
      })
      .addCase(fetchOtherViews.rejected, (state, action) => {
        state.error = action.error.message
        state.loading = false
      })

      // New reducers for the additional endpoints
      .addCase(fetchMonthlyPlatformBreakdown.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchMonthlyPlatformBreakdown.fulfilled, (state, action) => {
        state.monthlyPlatformBreakdown = action.payload
        state.loading = false
      })
      .addCase(fetchMonthlyPlatformBreakdown.rejected, (state, action) => {
        state.error = action.error.message
        state.loading = false
      })

      .addCase(fetchHourlyPropertyViews.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchHourlyPropertyViews.fulfilled, (state, action) => {
        state.hourlyPropertyViews = action.payload
        state.loading = false
      })
      .addCase(fetchHourlyPropertyViews.rejected, (state, action) => {
        state.error = action.error.message
        state.loading = false
      })

      .addCase(fetchHourlySessions.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchHourlySessions.fulfilled, (state, action) => {
        state.hourlySessions = action.payload
        state.loading = false
      })
      .addCase(fetchHourlySessions.rejected, (state, action) => {
        state.error = action.error.message
        state.loading = false
      })

      .addCase(fetchAllAnalytics.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAllAnalytics.fulfilled, (state, action) => {
        state.allAnalytics = action.payload
        state.loading = false
      })
      .addCase(fetchAllAnalytics.rejected, (state, action) => {
        state.error = action.error.message
        state.loading = false
      })

      .addCase(fetchWorldMap.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchWorldMap.fulfilled, (state, action) => {
        state.worldMap = action.payload // <-- add this field
        state.loading = false
      })
      .addCase(fetchWorldMap.rejected, (state, action) => {
        state.error = action.error.message
        state.loading = false
      })

      // KPI Data reducers
      .addCase(fetchKPIData.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchKPIData.fulfilled, (state, action) => {
        state.kpiData = action.payload.data
        state.loading = false
      })
      .addCase(fetchKPIData.rejected, (state, action) => {
        state.error = action.error.message
        state.loading = false
      })

      .addCase(fetchConversionByDevice.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchConversionByDevice.fulfilled, (state, action) => {
        state.conversionByDevice = action.payload.data || action.payload
        state.loading = false
      })
      .addCase(fetchConversionByDevice.rejected, (state, action) => {
        state.error = action.error.message
        state.loading = false
      })

      .addCase(fetchAppDownloads.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAppDownloads.fulfilled, (state, action) => {
        state.appDownloads = action.payload.data || action.payload
        state.loading = false
      })
      .addCase(fetchAppDownloads.rejected, (state, action) => {
        state.error = action.error.message
        state.loading = false
      })

      .addCase(fetchSalesConversionPath.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchSalesConversionPath.fulfilled, (state, action) => {
        state.salesConversionPath = action.payload.data
        state.loading = false
      })
      .addCase(fetchSalesConversionPath.rejected, (state, action) => {
        state.error = action.error.message
        state.loading = false
      })

      .addCase(fetchWebsiteAnalytics.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchWebsiteAnalytics.fulfilled, (state, action) => {
        const analyticsData = action.payload.data || action.payload
        if (Array.isArray(analyticsData)) {
          // Transform array format to object format
          const transformedData = {
            conversionRate: analyticsData.find(item => item.metric === 'Conversion rate')?.value?.replace('%', '') || 0,
            visitors: analyticsData.find(item => item.metric === 'Visitors')?.value || 0,
            pageViews: analyticsData.find(item => item.metric === 'Page views')?.value || 0,
            downloads: analyticsData.find(item => item.metric === 'Downloads')?.value || 0,
            clicks: analyticsData.find(item => item.metric === 'Clicks')?.value || 0
          }
          state.websiteAnalytics = transformedData
        } else {
          state.websiteAnalytics = analyticsData
        }
        state.loading = false
      })
      .addCase(fetchWebsiteAnalytics.rejected, (state, action) => {
        state.error = action.error.message
        state.loading = false
      })

      .addCase(fetchWebsiteMetrics.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchWebsiteMetrics.fulfilled, (state, action) => {
        const data = action.payload.data || action.payload
        if (Array.isArray(data)) {
          // Transform array format to object format
          const transformedData = {
            visits: data.find(item => item.metric === 'Visitors')?.value?.replace('k', '00') || 0,
            clicks: data.find(item => item.metric === 'Clicks')?.value || 0,
            downloads: data.find(item => item.metric === 'Downloads')?.value || 0,
            visitsGrowth: 0, // Growth data not provided in current API
            clicksGrowth: 0,
            downloadsGrowth: 0
          }
          state.websiteMetrics = transformedData
        } else {
          state.websiteMetrics = data
        }
        state.loading = false
      })
      .addCase(fetchWebsiteMetrics.rejected, (state, action) => {
        state.error = action.error.message
        state.loading = false
      })

      .addCase(fetchAppMetrics.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAppMetrics.fulfilled, (state, action) => {
        const data = action.payload.data || action.payload
        if (Array.isArray(data)) {
          // Transform array format to object format
          const transformedData = {
            downloads: data.find(item => item.metric === 'Downloads')?.value || 0,
            registered: data.find(item => item.metric === 'Registered')?.value || 0,
            clicks: data.find(item => item.metric === 'Clicks')?.value || 0,
            downloadsGrowth: 0, // Growth data not provided in current API
            registeredGrowth: 0,
            clicksGrowth: 0
          }
          state.appMetrics = transformedData
        } else {
          state.appMetrics = data
        }
        state.loading = false
      })
      .addCase(fetchAppMetrics.rejected, (state, action) => {
        state.error = action.error.message
        state.loading = false
      })

      .addCase(fetchUserTypeBreakdown.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchUserTypeBreakdown.fulfilled, (state, action) => {
        const data = action.payload.data || action.payload
        if (Array.isArray(data)) {
          // Store the raw array data for bar chart
          state.userTypeBreakdown = {
            chartData: data,
            mavenBuyer: data.reduce((sum, item) => sum + item.maven + item.buyer, 0),
            mavenBuyerGrowth: 0 // Growth data not provided in current API
          }
        } else {
          state.userTypeBreakdown = data
        }
        state.loading = false
      })
      .addCase(fetchUserTypeBreakdown.rejected, (state, action) => {
        state.error = action.error.message
        state.loading = false
      })
  },
})

export const {
  clearError
} = dashboardSlice.actions
export default dashboardSlice.reducer